// ============================================
// PLUPack · menú
// ============================================
// - Panel "Productos" de la compu: se abre al pasar el mouse por "Productos"
//   (o con la flecha abajo desde el teclado) y se cierra al salir o con Escape.
// - "Buscar" en el menú del celular: cierra el menú y abre el buscador.

// "Buscar": close the menu first (it releases its focus trap once closed), then open the search
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-plu-open-search]') : null;
  if (!button) return;
  event.preventDefault();
  const openSearch = () => {
    const search = /** @type {any} */ (document.getElementById('search-modal'));
    search?.showDialog?.();
  };
  const drawer = /** @type {any} */ (button.closest('header-drawer'));
  const details = drawer?.querySelector('details');
  if (!drawer || !details?.open) {
    openSearch();
    return;
  }
  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    observer.disconnect();
    window.clearTimeout(timer);
    openSearch();
  };
  const observer = new MutationObserver(() => {
    if (!details.open) finish();
  });
  observer.observe(details, { attributes: true, attributeFilter: ['open'] });
  const timer = window.setTimeout(finish, 700);
  drawer.close();
});

// ---------- desktop "Productos" panel ----------

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

class PluMegaMenu extends HTMLElement {
  /** @type {HTMLAnchorElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #item = null;
  #openTimer = 0;
  #closeTimer = 0;
  #rebindFrame = 0;
  /** @type {MutationObserver | null} */
  #observer = null;
  #media = window.matchMedia('(min-width: 750px) and (hover: hover)');

  connectedCallback() {
    this.panel = /** @type {HTMLElement} */ (this.querySelector('.plu-mega__panel'));
    if (!this.panel) return;
    this.#bind();
    // the header is rendered again after the page loads: bind to its new links
    this.#observer = new MutationObserver(this.#scheduleBind);
    this.#observer.observe(document.getElementById('header-group') ?? document.body, {
      childList: true,
      subtree: true,
    });
    this.addEventListener('pointerenter', this.#keep);
    this.addEventListener('pointerleave', this.#leave);
    this.addEventListener('focusout', this.#onFocusOut);
    this.#media.addEventListener('change', this.#onMediaChange);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#unbind();
    this.removeEventListener('pointerenter', this.#keep);
    this.removeEventListener('pointerleave', this.#leave);
    this.removeEventListener('focusout', this.#onFocusOut);
    this.#media.removeEventListener('change', this.#onMediaChange);
    this.#stopListening();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  #findTrigger() {
    const target = this.dataset.trigger;
    if (!target) return null;
    const path = new URL(target, window.location.origin).pathname.replace(/\/$/, '');
    for (const link of document.querySelectorAll('#header-group a.menu-list__link')) {
      if (!(link instanceof HTMLAnchorElement) || link.closest('.menu-drawer')) continue;
      if (new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') === path) return link;
    }
    return null;
  }

  #scheduleBind = () => {
    cancelAnimationFrame(this.#rebindFrame);
    this.#rebindFrame = requestAnimationFrame(this.#bind);
  };

  #bind = () => {
    const link = this.#findTrigger();
    if (link === this.#trigger) return;
    this.#unbind();
    if (!link) return;
    this.#trigger = link;
    this.#item = /** @type {HTMLElement} */ (link.closest('li') ?? link);
    link.setAttribute('aria-controls', this.panel?.id ?? '');
    link.setAttribute('aria-expanded', String(this.isOpen));
    link.setAttribute('data-plu-mega-trigger', '');
    this.#item.addEventListener('pointerenter', this.#enter);
    this.#item.addEventListener('pointerleave', this.#leave);
    link.addEventListener('keydown', this.#onTriggerKey);
  };

  #unbind() {
    if (this.#item) {
      this.#item.removeEventListener('pointerenter', this.#enter);
      this.#item.removeEventListener('pointerleave', this.#leave);
    }
    if (this.#trigger) {
      this.#trigger.removeEventListener('keydown', this.#onTriggerKey);
      this.#trigger.removeAttribute('aria-controls');
      this.#trigger.removeAttribute('aria-expanded');
      this.#trigger.removeAttribute('data-plu-mega-trigger');
    }
    this.#trigger = null;
    this.#item = null;
  }

  /** @param {PointerEvent} event */
  #enter = (event) => {
    if (event.pointerType === 'touch') return;
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    this.#openTimer = window.setTimeout(() => this.open(), this.isOpen ? 0 : 70);
  };

  #keep = () => {
    window.clearTimeout(this.#closeTimer);
  };

  #leave = () => {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(), 240);
  };

  /** @param {KeyboardEvent} event */
  #onTriggerKey = (event) => {
    if (event.key !== 'ArrowDown') return;
    event.preventDefault();
    this.open();
    requestAnimationFrame(() => {
      const first = this.panel?.querySelector(FOCUSABLE);
      if (first instanceof HTMLElement) first.focus();
    });
  };

  /** @param {KeyboardEvent} event */
  #onDocumentKey = (event) => {
    if (event.key !== 'Escape' || !this.isOpen) return;
    const hadFocus = this.contains(document.activeElement);
    this.close();
    if (hadFocus) this.#trigger?.focus();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const next = event.relatedTarget;
    if (next instanceof Node && (this.contains(next) || this.#trigger?.contains(next))) return;
    this.close();
  };

  #onMediaChange = () => {
    if (!this.#media.matches) this.close();
  };

  #place = () => {
    const header = document.getElementById('header-component');
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    this.style.setProperty('--plu-mega-top', `${Math.max(0, Math.round(bottom))}px`);
  };

  #stopListening() {
    document.removeEventListener('keydown', this.#onDocumentKey);
    window.removeEventListener('scroll', this.#place);
    window.removeEventListener('resize', this.#place);
  }

  open() {
    if (!this.#media.matches || !this.#trigger) return;
    window.clearTimeout(this.#closeTimer);
    this.#place();
    if (this.isOpen) return;
    this.classList.add('is-open');
    this.#trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.#onDocumentKey);
    window.addEventListener('scroll', this.#place, { passive: true });
    window.addEventListener('resize', this.#place, { passive: true });
  }

  close() {
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#stopListening();
  }
}

if (!customElements.get('plu-mega-menu')) {
  customElements.define('plu-mega-menu', PluMegaMenu);
}
